import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { useUserTier } from '../../hooks/useUserTier';
import { useSidebar } from '../../contexts/SidebarContext';
import ClientSelector from '../client/ClientSelector';

import { NotificationIcon } from '../notifications/NotificationIcon';
import ProfileDropdown from './ProfileDropdown';
import FreeCreditsModal from '../credits/FreeCreditsModal';
import { ThemeToggler } from '../ui/ThemeToggler';
import { Coins, Sparkles, Crown, Gift, Menu } from 'lucide-react';

interface HeaderProps {
  rightContent?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ rightContent }) => {
  const { user } = useAuth();
  const { currentTier, isFreeTier } = useUserTier();
  const { toggleMobileSidebar } = useSidebar();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFreeCreditsModal, setShowFreeCreditsModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`
      sticky top-0 z-[100] transition-all duration-300
      ${isScrolled 
        ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-lg shadow-gray-900/5' 
        : 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md'
      }
      border-b border-gray-200/50 dark:border-gray-700/50
    `}>
      <div className="px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          
          {/* Left Side - Mobile Menu Button + Logo */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileSidebar}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Logo */}
            <Link to="/app/dashboard" className="flex items-center group">
              <img 
                src="/logo-light.png" 
                alt="Quixocial" 
                className="h-6 sm:h-8 w-auto dark:brightness-150 dark:contrast-110 transition-all duration-300"
              />
            </Link>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
            
            {/* Credits Display & Free Credits Button */}
            {user && user.credits !== undefined && (
              <div className="flex items-center space-x-1 sm:space-x-2">
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowFreeCreditsModal(true)}
                  className="flex items-center space-x-1 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs font-medium shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden"
                  style={{ 
                    background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                    backgroundSize: '200% 200%',
                    backgroundPosition: 'left center',
                    boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundPosition = 'right center';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                    e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundPosition = 'left center';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  }}
                >
                  <Gift className="h-3 w-3" />
                  <span className="hidden lg:inline">Get Free Credits!</span>
                  <span className="lg:hidden">Free!</span>
                </motion.button>

                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center space-x-1 sm:space-x-2 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-amber-900/20 dark:to-yellow-900/20 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-emerald-200/50 dark:border-amber-700/50 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <Coins className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-600 dark:text-amber-400" />
                  <span className="font-bold text-xs sm:text-sm text-emerald-700 dark:text-amber-300">
                    {user.credits.toFixed(0)}
                  </span>
                  <span className="text-xs text-emerald-600/70 dark:text-amber-400/70 font-medium hidden lg:inline">
                    credits
                  </span>
                </motion.div>
              </div>
            )}

            {/* Notifications - Hidden on mobile */}
            <div className="relative hidden sm:block">
              <NotificationIcon />
            </div>

            {/* Theme Toggle - Desktop only */}
            <div className="hidden sm:block">
              <ThemeToggler />
            </div>

            {/* Client Selector - Hidden on mobile, shows simplified version for starter/creator tiers */}
            <div className="hidden sm:block">
              <ClientSelector />
            </div>


            
            {rightContent && <div className="hidden sm:flex items-center space-x-3">{rightContent}</div>}

            {/* Profile Dropdown */}
            <ProfileDropdown />
          </div>
        </div>
      </div>

      {/* Free Credits Modal */}
      <FreeCreditsModal 
        isOpen={showFreeCreditsModal}
        onClose={() => setShowFreeCreditsModal(false)}
      />
    </header>
  );
};

export default Header;
